import { publicIndicators } from "@/mocks/publico";

function MiniBar({ percent, color }: { percent: number; color: string }) {
  return (
    <div className="w-full bg-background-100 rounded-full h-2">
      <div
        className={`h-2 rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

export default function PublicIndicatorsPanel() {
  const { arvoresPorBairro, especiesFrequentes, estadoFitosSanitario, distribuicaoRisco, vistoriasMensais, co2PorArea } = publicIndicators;
  const maxArvores = Math.max(...arvoresPorBairro.map((b) => b.total));
  const maxVistorias = Math.max(...vistoriasMensais.map((v) => v.total));
  const maxCO2 = Math.max(...co2PorArea.map((c) => c.co2e));

  return (
    <section id="indicadores" className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">Indicadores Ambientais</h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Painel com os principais indicadores da arborização urbana: distribuição por bairro, espécies mais frequentes, estado fitossanitário e evolução mensal.
          </p>
        </div>

        {/* Row 1: Árvores por bairro + Espécies */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Árvores por bairro */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Árvores por bairro</h3>
            <div className="space-y-3">
              {arvoresPorBairro.map((b) => (
                <div key={b.bairro}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-foreground-600">{b.bairro}</span>
                    <span className="text-xs font-semibold text-foreground-900">{b.total}</span>
                  </div>
                  <MiniBar percent={(b.total / maxArvores) * 100} color="bg-primary-500" />
                </div>
              ))}
            </div>
          </div>

          {/* Espécies mais frequentes */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Espécies mais frequentes</h3>
            <div className="space-y-3">
              {especiesFrequentes.map((e) => (
                <div key={e.especie}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-foreground-600">{e.especie}</span>
                    <span className="text-xs font-semibold text-foreground-900">{e.total} ({e.percent}%)</span>
                  </div>
                  <MiniBar percent={e.percent * 5} color="bg-accent-500" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: Fitossanitário + Risco */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {/* Estado Fitossanitário */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Estado Fitossanitário</h3>
            <div className="space-y-3">
              {estadoFitosSanitario.map((e) => (
                <div key={e.estado}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-foreground-600">{e.estado}</span>
                    <span className="text-xs font-semibold text-foreground-900">{e.total} ({e.percent}%)</span>
                  </div>
                  <MiniBar
                    percent={e.percent}
                    color={
                      e.estado === "Bom" ? "bg-primary-500" :
                      e.estado === "Regular" ? "bg-caution-500" :
                      e.estado === "Ruim" ? "bg-warning-500" :
                      "bg-danger-500"
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Distribuição por Risco */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Distribuição por Risco</h3>
            <div className="space-y-3">
              {distribuicaoRisco.map((r) => (
                <div key={r.risco}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-foreground-600">Risco {r.risco}</span>
                    <span className="text-xs font-semibold text-foreground-900">{r.total} ({r.percent}%)</span>
                  </div>
                  <MiniBar
                    percent={r.percent}
                    color={
                      r.risco === "Baixo" ? "bg-primary-500" :
                      r.risco === "Médio" ? "bg-caution-500" :
                      r.risco === "Alto" ? "bg-warning-500" :
                      "bg-danger-500"
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Vistorias mensais + CO2e */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vistorias mensais */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Vistorias realizadas por mês (2026)</h3>
            <div className="flex items-end gap-3 h-32 justify-center">
              {vistoriasMensais.map((v) => (
                <div key={v.mes} className="flex flex-col items-center gap-1">
                  <span className="text-xs font-semibold text-foreground-900">{v.total}</span>
                  <div
                    className="w-10 md:w-14 bg-primary-500 rounded-t-lg transition-all duration-300"
                    style={{ height: `${(v.total / maxVistorias) * 100}%`, minHeight: "8px" }}
                  />
                  <span className="text-xs text-foreground-400">{v.mes}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CO2e por área */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">
              Estimativa preliminar de CO₂e por área (t)
            </h3>
            <div className="space-y-3">
              {co2PorArea.map((c) => (
                <div key={c.area}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-foreground-600">{c.area}</span>
                    <span className="text-xs font-semibold text-foreground-900">{c.co2e} t</span>
                  </div>
                  <MiniBar percent={(c.co2e / maxCO2) * 100} color="bg-accent-500" />
                </div>
              ))}
            </div>
            <p className="text-xs text-foreground-400 italic mt-3">
              Estimativas preliminares. Não representam certificação oficial de crédito de carbono.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}